// ============================================================================
// SYUT SCOPED EMPLOYEE SELECT
// ============================================================================
// Canonical Employee/Manager dropdown for transaction screens, list filters,
// report filters, and dashboards. Always sources its options from the server,
// scoped to what the caller is authorized to act on (manage-*) or see (view-*)
// for the supplied moduleId.
//
//   scopeMode="action"  →  /Employees/scoped   (manage-* ladder)
//   scopeMode="view"    →  /Employees/visible  (view-*   ladder)
//
// The backend is the single source of truth — the previous client-side BFS
// team-walk used in WIP / EAR / Manager Dashboard is gone. There is no
// fallback path: if the user has no scope on the supplied moduleId, the
// dropdown is empty (which is correct).

import React from 'react';
import { SyutSelect } from '../SyutSelect';
import { employeeService, type ScopedModuleId } from 'api/services/employeeService';

export type ScopeMode = 'action' | 'view';
export type ScopedEmployeeRole = 'manager' | 'partner' | 'any';

export interface SyutScopedEmployeeSelectProps {
  value?: string;
  onChange: (value: string) => void;

  /** Which scope ladder drives the result set. */
  scopeMode: ScopeMode;
  /** The module the dropdown belongs to (drives backend permission lookup). */
  moduleId: ScopedModuleId;

  /** Optional role filter on top of scope ('manager', 'partner', or 'any'). */
  role?: ScopedEmployeeRole;

  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Seed options used to resolve a saved value's label in edit mode. */
  seedOptions?: { value: string; label: string }[];
  includeInactive?: boolean;
  includeLeft?: boolean;
  pageSize?: number;
  disabled?: boolean;
}

export const SyutScopedEmployeeSelect: React.FC<SyutScopedEmployeeSelectProps> = ({
  value,
  onChange,
  scopeMode,
  moduleId,
  role = 'any',
  label,
  error,
  required,
  isClearable = true,
  placeholder,
  size = 'sm',
  seedOptions,
  includeInactive = false,
  includeLeft = false,
  pageSize = 500,
  disabled,
}) => {
  const defaultPlaceholder =
    placeholder ??
    (role === 'manager'
      ? 'Type 3 letters to search managers...'
      : role === 'partner'
      ? 'Type 3 letters to search partners...'
      : 'Type 3 letters to search employees...');

  return (
    <SyutSelect
      label={label}
      value={value}
      isAsync
      loadOptions={async (input: string) => {
        const fetcher = scopeMode === 'action' ? employeeService.getScoped : employeeService.getVisible;
        const res = await fetcher.call(employeeService, {
          moduleId,
          page: 1,
          pageSize,
          searchTerm: input || undefined,
          isManager: role === 'manager' ? true : undefined,
          isPartner: role === 'partner' ? true : undefined,
          includeInactive,
          includeLeft,
        });
        return res.data.map((e: any) => ({
          value: e.id,
          label: `${e.name || ''}${e.code ? ` (${e.code})` : ''}`.trim(),
        }));
      }}
      defaultOptions
      cacheOptions={false}
      seedOptions={seedOptions}
      debounceDelay={300}
      minSearchLength={3}
      isClearable={isClearable}
      size={size}
      placeholder={defaultPlaceholder}
      error={error}
      required={required}
      isDisabled={disabled}
      onChange={(val) => onChange((val as string) || '')}
    />
  );
};

export default SyutScopedEmployeeSelect;
